import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/Toast';
import { useConfirm } from '../../components/Modal';
import TabBar from '../../components/TabBar';
import AdminNotificationBell from '../../components/AdminNotificationBell';
import BizSwitch from '../../components/BizSwitch';
import api from '../../utils/api';
import {
  ArrowLeft, Film, FileText, Upload, Trash2, Save, PlayCircle,
  X, Loader, ImageIcon, ArrowUp, ArrowDown, Plus
} from 'lucide-react';

// 教程设置：管理员分别配置「业务流程」「录屏教程」两套内容
// 业务流程：文字说明 + 多张流程图（最多 10 张/批）
// 录屏教程：文字说明 + 后续可扩展
const SECTIONS = [
  { key: 'submit',    label: '业务流程', desc: '前台"教程 → 业务流程"展示：文字 + 多张流程图', icon: FileText },
  { key: 'recording', label: '录屏教程', desc: '前台"教程 → 录屏教程"展示：文字说明', icon: PlayCircle },
];

export default function TutorialSettingsPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const confirm = useConfirm();
  // 排五 / 证券 两套教程分开编辑保存：data[biz] = { submit, recording }
  const emptySet = () => ({ submit: { text: '', images: [] }, recording: { text: '', images: [] } });
  const [data, setData] = useState({ legacy: emptySet(), puzzle: emptySet() });
  const [biz, setBiz] = useState('legacy');
  const sections = data[biz];
  const setSections = (fn) => setData(d => ({ ...d, [biz]: typeof fn === 'function' ? fn(d[biz]) : fn }));
  const bizQ = biz === 'puzzle' ? '&biz=puzzle' : '';                 // 接口参数
  const slot = (k) => (biz === 'puzzle' ? `puzzle_${k}` : k);         // 服务端存储目录名
  const [activeKey, setActiveKey] = useState('submit');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState([]); // 待上传的 File 列表（业务流程：多张图）
  const [file, setFile] = useState(null);       // 待上传的 File（录屏教程：单文件视频）
  const [loading, setLoading] = useState(true);
  // 灯箱：{ items: [imageUrl], idx } 用于预览已上传的图
  const [lightbox, setLightbox] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/admin/settings/tutorial');
      const d = res.data || {};
      const norm = (src) => ({
        // 业务流程：多张图（images）；旧版可能残留 videoUrl 也兼容
        submit: {
          text:     src?.submit?.text || '',
          images:   src?.submit?.images || [],
          videoUrl: src?.submit?.videoUrl || '',
        },
        // 录屏教程：单文件视频
        recording: {
          text:     src?.recording?.text || '',
          videoUrl: src?.recording?.videoUrl || '',
          images:   src?.recording?.images || [],
        },
      });
      if (d.submit || d.recording) {
        setData({ legacy: norm(d), puzzle: norm(d.puzzle) });
      } else {
        // 旧格式：{ text, videoUrl } → 合并到 submit
        setData({
          legacy: { submit: { text: d.text || '', images: [], videoUrl: d.videoUrl || '' }, recording: { text: '', images: [], videoUrl: '' } },
          puzzle: norm(null),
        });
      }
    } catch {
      showToast('加载失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // 切换标签时清空待上传文件
  useEffect(() => { setFileList([]); setFile(null); }, [activeKey, biz]);

  const current = sections[activeKey] || { text: '', images: [], videoUrl: '' };
  const isRecording = activeKey === 'recording';
  const updateCurrent = (patch) => {
    setSections(s => ({ ...s, [activeKey]: { ...s[activeKey], ...patch } }));
  };

  // 文字保存
  const handleSaveText = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/settings/tutorial/text?type=${activeKey}${bizQ}`, { text: current.text });
      showToast('文字说明已保存', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || '保存失败', 'error');
    } finally {
      setSaving(false);
    }
  };

  // 选择待上传的图片（业务流程 tab：可多选，10 张限制）
  const handleFileSelect = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const valid = picked.filter(f => f.type.startsWith('image/'));
    if (valid.length < picked.length) showToast('已过滤非图片文件', 'error');
    const overflow = fileList.length + valid.length > 10;
    const next = [...fileList, ...valid].slice(0, 10);
    setFileList(next);
    if (overflow) showToast('单批最多 10 张，多余已截断', 'error');
    e.target.value = '';
  };
  const removeFile = (i) => setFileList(f => f.filter((_, j) => j !== i));

  // 业务图片批量上传
  const handleUploadImages = async () => {
    if (!fileList.length) { showToast('请先选择图片', 'error'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fileList.forEach(f => fd.append('images', f));
      const res = await api.post(`/admin/settings/tutorial/image?type=${activeKey}${bizQ}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      updateCurrent({ images: res.data.images || [] });
      setFileList([]);
      showToast(`上传成功 ${fileList.length} 张`, 'success');
    } catch (err) {
      showToast(err.response?.data?.error || '上传失败', 'error');
    } finally {
      setUploading(false);
    }
  };

  // 删除单张业务图
  const handleDeleteImage = async (img) => {
    const ok = await confirm(`确认删除「${img.originalName || '该图片'}」？`, { danger: true });
    if (!ok) return;
    try {
      const res = await api.delete(`/admin/settings/tutorial/image?type=${activeKey}${bizQ}`, { data: { filename: img.filename } });
      updateCurrent({ images: res.data.images || [] });
      showToast('已删除', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || '删除失败', 'error');
    }
  };

  // 业务图调序
  const moveImage = async (idx, delta) => {
    const list = [...current.images];
    const newIdx = idx + delta;
    if (newIdx < 0 || newIdx >= list.length) return;
    [list[idx], list[newIdx]] = [list[newIdx], list[idx]];
    updateCurrent({ images: list });
    try {
      await api.put(`/admin/settings/tutorial/image/order?type=${activeKey}${bizQ}`, { order: list.map(i => i.filename) });
    } catch (err) {
      showToast('顺序同步失败', 'error');
    }
  };

  // 视频上传（仅录屏教程 tab）：单文件 45MB
  const handleUploadVideo = async () => {
    if (!file) { showToast('请先选择视频文件', 'error'); return; }
    if (file.size > 45 * 1024 * 1024) { showToast('视频不能超过 45MB', 'error'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('video', file);
      const res = await api.post(`/admin/settings/tutorial/video?type=${activeKey}${bizQ}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      updateCurrent({ videoUrl: res.data.videoUrl || '' });
      setFile(null);
      showToast('视频已上传', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || '上传失败', 'error');
    } finally {
      setUploading(false);
    }
  };

  // 视频删除（仅录屏教程 tab）
  const handleDeleteVideo = async () => {
    const ok = await confirm('确认删除当前视频？', { danger: true });
    if (!ok) return;
    try {
      await api.delete(`/admin/settings/tutorial/video?type=${activeKey}${bizQ}`);
      updateCurrent({ videoUrl: '' });
      setFile(null);
      showToast('视频已删除', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || '删除失败', 'error');
    }
  };

  // 当前块已上传图的 URL
  const imageUrls = current.images.map(im => ({
    url: `/uploads/tutorial/${slot(activeKey)}/${im.filename}`,
    originalName: im.originalName
  }));

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px 4px 0', display: 'flex', alignItems: 'center', color: 'var(--c-primary)' }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ flex: 1 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
            <Film size={16} /> 教程设置
          </span>
        </span>
        <AdminNotificationBell />
      </div>

      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {loading ? (
          <p style={{ textAlign: 'center', padding: 40, color: 'var(--c-text-secondary)' }}>加载中...</p>
        ) : (
          <>
            {/* 业务切换：排五 / 证券 两套教程分开编辑 */}
            <BizSwitch value={biz} onChange={setBiz} />

            {/* 标签头 */}
            <div style={{
              display: 'flex', gap: 6,
              padding: 4,
              background: 'var(--c-bg)', borderRadius: 12
            }}>
              {SECTIONS.map(s => {
                const Icon = s.icon;
                const active = s.key === activeKey;
                return (
                  <button
                    key={s.key}
                    onClick={() => setActiveKey(s.key)}
                    style={{
                      flex: 1, height: 40, borderRadius: 10,
                      border: 'none', cursor: 'pointer',
                      background: active ? 'var(--c-surface)' : 'transparent',
                      color: active ? 'var(--c-primary)' : 'var(--c-text-secondary)',
                      fontSize: 14, fontWeight: active ? 700 : 500,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      boxShadow: active ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
                      transition: 'all 0.15s'
                    }}
                  >
                    <Icon size={15} /> {s.label}
                  </button>
                );
              })}
            </div>

            {/* 文字教程 */}
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <FileText size={16} color="var(--c-primary)" />
                <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>文字说明</h3>
              </div>
              <p style={{ fontSize: 12, color: 'var(--c-text-secondary)', margin: '0 0 10px' }}>
                {SECTIONS.find(s => s.key === activeKey)?.desc}
              </p>
              <textarea
                className="input"
                value={current.text}
                onChange={e => updateCurrent({ text: e.target.value })}
                placeholder="例如：业务流程：1. 接量获取任务；2. 复制手机号；3. 加好友；4. 完成回填并上传凭证…"
                style={{ minHeight: 120, fontSize: 14, lineHeight: 1.7, resize: 'vertical' }}
              />
              <button className="btn btn-primary" onClick={handleSaveText} disabled={saving}
                style={{ width: '100%', height: 44, marginTop: 10 }}>
                <Save size={16} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                {saving ? '保存中...' : '保存文字说明'}
              </button>
            </div>

            {/* 流程图（仅业务流程 tab 显示） */}
            {activeKey === 'submit' && (
              <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <ImageIcon size={16} color="var(--c-primary)" />
                  <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>流程图（{current.images.length}）</h3>
                </div>
                <p style={{ fontSize: 12, color: 'var(--c-text-secondary)', margin: '0 0 10px' }}>
                  支持 jpg/png/webp，单张 ≤ 5MB，最多 10 张/批；按列表顺序展示，可上/下移调序
                </p>

                {/* 已上传图列表 */}
                {current.images.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                    {current.images.map((img, idx) => (
                      <div key={img.filename} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 10px', background: 'var(--c-bg)',
                        borderRadius: 10, border: '1px solid var(--c-border)'
                      }}>
                        {/* 缩略图：点击放大 */}
                        <img
                          src={`/uploads/tutorial/${slot(activeKey)}/${img.filename}`}
                          alt=""
                          onClick={() => setLightbox({ items: imageUrls, idx })}
                          style={{
                            width: 56, height: 56, objectFit: 'cover', borderRadius: 8,
                            cursor: 'zoom-in', flexShrink: 0
                          }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {idx + 1}. {img.originalName || img.filename}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button
                            onClick={() => moveImage(idx, -1)}
                            disabled={idx === 0}
                            title="上移"
                            style={{
                              width: 30, height: 30, borderRadius: 6, padding: 0,
                              background: 'transparent', border: '1px solid var(--c-border)',
                              cursor: idx === 0 ? 'not-allowed' : 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              opacity: idx === 0 ? 0.4 : 1
                            }}
                          ><ArrowUp size={14} /></button>
                          <button
                            onClick={() => moveImage(idx, 1)}
                            disabled={idx === current.images.length - 1}
                            title="下移"
                            style={{
                              width: 30, height: 30, borderRadius: 6, padding: 0,
                              background: 'transparent', border: '1px solid var(--c-border)',
                              cursor: idx === current.images.length - 1 ? 'not-allowed' : 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              opacity: idx === current.images.length - 1 ? 0.4 : 1
                            }}
                          ><ArrowDown size={14} /></button>
                          <button
                            onClick={() => handleDeleteImage(img)}
                            title="删除"
                            style={{
                              width: 30, height: 30, borderRadius: 6, padding: 0,
                              background: 'rgba(255,59,48,0.1)', border: '1px solid rgba(255,59,48,0.3)',
                              cursor: 'pointer', color: 'var(--c-danger)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}
                          ><Trash2 size={14} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    border: '1px dashed var(--c-border)', borderRadius: 10,
                    padding: 24, textAlign: 'center', color: 'var(--c-text-tertiary)',
                    fontSize: 13, marginBottom: 12
                  }}>
                    <ImageIcon size={28} style={{ marginBottom: 6 }} />
                    <div>尚未上传流程图</div>
                  </div>
                )}

                {/* 待上传预览 */}
                {fileList.length > 0 && (
                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6,
                    marginBottom: 12
                  }}>
                    {fileList.map((f, i) => (
                      <div key={i} style={{ position: 'relative', aspectRatio: '1' }}>
                        <img src={URL.createObjectURL(f)} alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, border: '1px solid var(--c-border)' }} />
                        <button onClick={() => removeFile(i)} style={{
                          position: 'absolute', top: -6, right: -6,
                          width: 20, height: 20, borderRadius: '50%', padding: 0,
                          background: 'rgba(255,59,48,0.9)', border: '1.5px solid white',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          <X size={10} color="white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* 选择 + 上传按钮 */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <label className="btn btn-outline" style={{
                    flex: 1, height: 42, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 6, cursor: 'pointer'
                  }}>
                    <Plus size={15} /> 选择图片
                    <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                      onChange={handleFileSelect} />
                  </label>
                  <button
                    className="btn btn-primary"
                    style={{ flex: 1, height: 42 }}
                    onClick={handleUploadImages}
                    disabled={uploading || fileList.length === 0}
                  >
                    {uploading ? <><Loader size={14} className="spin" /> 上传中...</> : <><Upload size={14} /> 上传 {fileList.length || ''} 张</>}
                  </button>
                </div>
              </div>
            )}

            {/* 录屏视频（仅录屏教程 tab） */}
            {isRecording && (
              <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <PlayCircle size={16} color="var(--c-primary)" />
                  <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>视频演示</h3>
                </div>
                <p style={{ fontSize: 12, color: 'var(--c-text-secondary)', margin: '0 0 10px' }}>
                  支持 mp4/webm，单文件不超过 45MB；上传后自动替换旧视频
                </p>

                {current.videoUrl && (
                  <video
                    key={current.videoUrl}
                    src={current.videoUrl}
                    controls
                    playsInline
                    style={{ width: '100%', borderRadius: 12, background: '#000', marginBottom: 10 }}
                  />
                )}

                {!current.videoUrl && (
                  <div style={{
                    border: '1px dashed var(--c-border)', borderRadius: 10,
                    padding: 24, textAlign: 'center', color: 'var(--c-text-tertiary)',
                    fontSize: 13, marginBottom: 12
                  }}>
                    <Film size={28} style={{ marginBottom: 6 }} />
                    <div>尚未上传视频</div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8 }}>
                  <label className="btn btn-outline" style={{
                    flex: 1, height: 42, display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 6, cursor: 'pointer'
                  }}>
                    <Upload size={15} /> {file ? file.name : '选择视频'}
                    <input type="file" accept="video/*" style={{ display: 'none' }}
                      onChange={e => setFile(e.target.files?.[0] || null)} />
                  </label>
                  <button
                    className="btn btn-primary"
                    style={{ flex: 1, height: 42 }}
                    onClick={handleUploadVideo}
                    disabled={uploading || !file}
                  >
                    {uploading ? '上传中...' : (file ? '上传视频' : current.videoUrl ? '替换视频' : '上传')}
                  </button>
                </div>
                {current.videoUrl && (
                  <button
                    onClick={handleDeleteVideo}
                    style={{
                      width: '100%', height: 42, marginTop: 8,
                      background: 'transparent',
                      color: 'var(--c-danger)',
                      border: '1px solid rgba(255,59,48,0.35)',
                      borderRadius: 8, fontSize: 14, fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
                    }}
                  >
                    <Trash2 size={14} />删除视频
                  </button>
                )}
              </div>
            )}

            <p style={{ fontSize: 12, color: 'var(--c-text-tertiary)', textAlign: 'center' }}>
              前台用户登录后点击右上角「教程」即可查看，标签页内分别展示两套内容
            </p>
          </>
        )}
      </div>

      {/* 灯箱：点击缩略图全屏预览（portal 到 body + 顶级 z-index，避免被 #root 容器或抽屉遮挡） */}
      {lightbox && createPortal(
        <div onClick={() => setLightbox(null)} style={{
          position: 'fixed', inset: 0, zIndex: 2147483600,
          background: 'rgba(0,0,0,0.92)', display: 'flex',
          flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16
        }}>
          <button onClick={() => setLightbox(null)} style={{
            position: 'absolute', top: 'calc(16px + env(safe-area-inset-top, 0px))', right: 'calc(16px + env(safe-area-inset-right, 0px))',
            background: 'rgba(255,255,255,0.1)', border: 'none',
            borderRadius: '50%', width: 36, height: 36, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <X size={18} color="white" />
          </button>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 12 }}>
            {lightbox.idx + 1} / {lightbox.items.length}
          </div>
          <img src={lightbox.items[lightbox.idx].url} alt=""
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '92vw', maxHeight: '72vh', objectFit: 'contain', borderRadius: 12 }} />
          {lightbox.items[lightbox.idx].originalName && (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 10 }}>
              {lightbox.items[lightbox.idx].originalName}
            </div>
          )}
        </div>,
        document.body
      )}

      <TabBar mode="admin" />
    </div>
  );
}